import { styled, css } from 'styled-components';
import { motion } from 'framer-motion';

export const Container = styled.div`
  position: relative;
  display: inline-flex;
`;

export const Trigger = styled.button<{ $hasValue: boolean }>`
  ${({ theme: { borderRadius, colors, fontSizes, fontWeights, motionSpeeds, motions }, $hasValue }) => css`
    align-items: center;
    background: ${colors.brandLight};
    border: 1px solid ${colors.brandDarkAlpha20};
    border-radius: ${borderRadius.sm};
    box-sizing: border-box;
    color: ${$hasValue ? colors.brandDark : colors.brandDarkTint50};
    cursor: pointer;
    display: flex;
    font-family: inherit;
    font-size: ${fontSizes.s14};
    font-weight: ${fontWeights.regular};
    gap: 0.5rem;
    height: 2.5rem;
    justify-content: space-between;
    outline: 2px solid transparent;
    padding: 0 0.75rem;
    transition: all ${motionSpeeds.fast} ${motions.default};
    user-select: none;
    width: 100%;

    .icon {
      flex-shrink: 0;
      height: 1rem;
      width: 1rem;
    }

    &:hover {
      border-color: ${colors.brandDarkAlpha40};
    }

    &:focus-visible,
    &:focus {
      border-color: ${colors.brandPrimary};
      outline-color: ${colors.brandPrimaryAlpha20};
      outline-offset: 2px;
    }

    &:disabled {
      background: ${colors.brandDarkAlpha5};
      color: ${colors.brandDarkTint50};
      cursor: not-allowed;
    }
  `}
`;

export const CalendarDropdown = styled(motion.div)`
  ${({ theme: { borderRadius, colors } }) => css`
    background: ${colors.brandLight};
    border-radius: ${borderRadius.sm};
    box-shadow:
      0 0 0 1px ${colors.brandDarkTint80},
      0 0.75rem 0.5rem -0.25rem rgba(32, 30, 36, 0.15),
      0 0.25rem 0.25rem -0.125rem rgba(32, 30, 36, 0.2);
    padding: 0.75rem;
    user-select: none;
    width: 16rem;
    z-index: 999;
  `}
`;

export const CalendarHeader = styled.div`
  ${({ theme: { colors, fontSizes, fontWeights } }) => css`
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    padding: 0.25rem 0;

    span {
      color: ${colors.brandDark};
      font-size: ${fontSizes.s14};
      font-weight: ${fontWeights.medium};
    }
  `}
`;

export const NavButton = styled.button`
  ${({ theme: { borderRadius, colors, motionSpeeds, motions } }) => css`
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: ${borderRadius.sm};
    cursor: pointer;
    display: flex;
    height: 1.75rem;
    justify-content: center;
    outline: 2px solid transparent;
    padding: 0;
    transition: all ${motionSpeeds.fast} ${motions.default};
    width: 1.75rem;

    .icon {
      height: 1rem;
      width: 1rem;
    }

    &:hover {
      background: ${colors.brandDarkAlpha5};
    }

    &:focus-visible {
      outline-color: ${colors.brandPrimaryAlpha50};
      outline-offset: 2px;
    }
  `}
`;

export const WeekDaysRow = styled.div`
  ${({ theme: { colors, fontSizes, fontWeights } }) => css`
    display: grid;
    gap: 0;
    grid-template-columns: repeat(7, 1fr);
    margin-bottom: 0.25rem;

    span {
      color: ${colors.brandDarkTint50};
      font-size: ${fontSizes.s12};
      font-weight: ${fontWeights.medium};
      padding: 0.25rem;
      text-align: center;
    }
  `}
`;

export const DaysGrid = styled.div`
  display: grid;
  gap: 0;
  grid-template-columns: repeat(7, 1fr);
`;

export const DayCell = styled.button<{
  $isCurrentMonth: boolean;
  $isDisabled: boolean;
  $isSelected: boolean;
  $isToday: boolean;
  $isInRange: boolean;
  $isRangeStart: boolean;
  $isRangeEnd: boolean;
}>`
  ${({
    theme: { borderRadius, colors, fontSizes, fontWeights, motionSpeeds, motions },
    $isCurrentMonth,
    $isDisabled,
    $isSelected,
    $isToday,
    $isInRange,
    $isRangeStart,
    $isRangeEnd,
  }) => css`
    align-items: center;
    aspect-ratio: 1;
    background: transparent;
    border: 0;
    border-radius: ${borderRadius.sm};
    color: ${$isCurrentMonth ? colors.brandDark : colors.brandDarkTint70};
    cursor: pointer;

    ${
      $isDisabled &&
      css`
        color: ${colors.brandDarkTint70};
        cursor: not-allowed;
        opacity: 0.35;
        pointer-events: none;
      `
    }
    display: flex;
    font-family: inherit;
    font-size: ${fontSizes.s12};
    font-weight: ${fontWeights.regular};
    justify-content: center;
    outline: 2px solid transparent;
    padding: 0;
    transition:
      background ${motionSpeeds.fast} ${motions.default},
      color ${motionSpeeds.fast} ${motions.default};

    ${
      $isToday &&
      css`
        font-weight: ${fontWeights.semiBold};
        box-shadow: inset 0 0 0 1px ${colors.brandDarkAlpha20};
      `
    }

    ${
      $isInRange &&
      !$isSelected &&
      css`
        background: ${colors.brandPrimaryAlpha10};
        border-radius: 0;
      `
    }

    ${
      $isRangeStart &&
      css`
        border-radius: ${borderRadius.sm} 0 0 ${borderRadius.sm};
      `
    }

    ${
      $isRangeEnd &&
      css`
        border-radius: 0 ${borderRadius.sm} ${borderRadius.sm} 0;
      `
    }

    ${
      $isSelected &&
      css`
        background: ${colors.brandPrimary};
        color: ${colors.brandLight};
        font-weight: ${fontWeights.medium};
      `
    }

    &:hover {
      ${
        !$isSelected &&
        css`
          background: ${colors.brandDarkAlpha5};
        `
      }
    }

    &:focus-visible {
      outline-color: ${colors.brandPrimaryAlpha50};
      outline-offset: 2px;
      z-index: 1;
    }
  `}
`;
